import React from "react";
import {
    DataGrid,
} from "@mui/x-data-grid";
import {
    Box,
    Button,
    Chip,
    Modal,
    FormControl,
    Autocomplete,
    TextField,
    Typography,
} from "@mui/material";
import {
    AlertCircle,
} from 'react-feather';
import { useDispatch, useSelector } from "react-redux";
import ReviewPage from "../billing/Validation";
import './plans.css';
import BotmarkEstimateView from "../billing/BotmarkEstimateView";
import StaticValidationView from './StaticValidationView';
import dayjs from 'dayjs';
import { getPlanTableJobStatusSortLabel, resolvePlanTableJobStatus } from '../../utils/planJobStatus';
import { PlanTableJobStatusCell } from './PlanTableJobStatusCell';
import { sortByString } from '../../utils/sortByString';
import AgreementModal from '../../components/AgreementModal';
import { StorageLimitIndicator } from '../../components/StorageLimitIndicator';
import { useQueryClient } from '@tanstack/react-query';
import { useCompanyFiles, useDeleteFiles, useFileCount, useUpdateFileName, useFetchValidationFile, useDownloadPaid, useFetchPolicyPdf } from '../../hooks/useFiles';
import { useUpdateInvoiceStatus } from '../../hooks/useInvoices';
import { canAccess } from '../../permissions/canAccess';
import { AppViewIds } from '../../permissions/appViewIds';
import { queryKeys } from '../../query/queryKeys';
import { EMPTY_GUID, NA_CUSTOMER_OPTION, customerAutocompleteOptions, isCustomerOptionEqual } from '../../query/queryConfig';
import { useAdminCompany } from '../../context/AdminCompanyContext';
import { useSAConfig } from '../../hooks/useSAConfig';
import { useCompanyMessages } from '../../hooks/useMessages';
import { useUpdateAgreement } from '../../hooks/useAuth';
import { usePermissionState } from '../../stores/permissionStore';

const PlanTable = ({managePlans}) => {
    /// displays all validated plans for a processor

    const { companyData } = useAdminCompany();

    const dispatch = useDispatch();
    const updateFileName = useUpdateFileName();
    const updateInvoiceStatus = useUpdateInvoiceStatus();
    const fetchValidationFile = useFetchValidationFile();
    const downloadPaid = useDownloadPaid();
    const fetchPolicyPdf = useFetchPolicyPdf();
    const updateAgreement = useUpdateAgreement();
    const permissionState = usePermissionState();
    const canManagePlans = canAccess(AppViewIds.PlansAdmin, permissionState);
    const [review, setReview] = React.useState(false);
    const [pendingBotmarkEstimateFileId, setPendingBotmarkEstimateFileId] = React.useState(null);
    const company = useSelector((state) => state.CompanyReducer.userCompanies[0]);
    const [paginationModel, setPaginationModel] = React.useState({page: 0, pageSize: 100});
    const [openBotmarkEstimate, setOpenBotmarkEstimate] = React.useState(false);
    const [botmarkEstimateObj, setBotmarkEstimateObj] = React.useState({});
    const [validationView, setValidationView] = React.useState(false);
    const pauseOverlays = review || validationView || openBotmarkEstimate;

    const isMountedRef = React.useRef(true);
    React.useEffect(() => {
        isMountedRef.current = true;
        return () => { isMountedRef.current = false; };
    }, []);

    const { data: saConfigData = [] } = useSAConfig({ pauseRefetch: pauseOverlays });
    const [internalAnn, setInternalAnn] = React.useState();
    function initValues(){
        for (let i = 0; i < saConfigData.length; i++){
            if (saConfigData[i].configName == 'Internal Announcements'){
                if (isMountedRef.current) {
                    setInternalAnn(saConfigData[i].configValue)
                }
            }
        }
    }

    const [ selectedCustomer, setSelectedCustomer ] = React.useState(NA_CUSTOMER_OPTION);

    const companyId = managePlans === true ? companyData?.id : company?.id;
    const { data: managePlansCustomers = [] } = useCompanyMessages({
        companyId,
        enabled: Boolean(managePlans && companyId),
        pauseRefetch: pauseOverlays,
    });

    const allCustomers = managePlans === true ? managePlansCustomers : [];
    let allCustomersArray = sortByString(allCustomers ?? [], 'companyContact');

    const defaultProps = {
        options: customerAutocompleteOptions(allCustomersArray),
        getOptionLabel: (option) => option.companyContact ?? '',
        isOptionEqualToValue: isCustomerOptionEqual,
    };

    React.useEffect(() => {
        initValues();
    },[saConfigData]);

    const customerSelect = useSelector((state) => state.UserReducer.customerSelection);

    const customerId = managePlans === true ? selectedCustomer?.userCompanyId : customerSelect?.userCompanyId;

    const queryClient = useQueryClient();
    const refreshCompanyFiles = React.useCallback(() => {
        void queryClient.invalidateQueries({ queryKey: queryKeys.companyFiles.all() });
    }, [queryClient]);

    const { data: companyFilesData } = useCompanyFiles({
        companyId,
        customerId,
        page: paginationModel.page,
        pageSize: paginationModel.pageSize,
        pauseRefetch: pauseOverlays,
    });

    const files = companyFilesData?.filesDTO ?? [];
    const filesCount = companyFilesData?.totalFileCount ?? 0;

    const rowCountRef = React.useRef(filesCount || 0);

    const rowCount = React.useMemo(() => {
        if (filesCount !== undefined) {
            rowCountRef.current = filesCount;
        }
        return rowCountRef.current;
    }, [filesCount]);

    let selectedCompanyValues = {};
    let selectedCustomerValues = {};
    let custId = "";
    if(managePlans == true){
        selectedCompanyValues = companyData;
        selectedCustomerValues = selectedCustomer;
        custId = EMPTY_GUID;
    } else {
        selectedCompanyValues = company;
        selectedCustomerValues = customerSelect;
        custId = customerSelect.userCompanyId;
    };

    const { data: fileStorageCount = 0 } = useFileCount({
        companyId,
        enabled: Boolean(companyId),
        notify: !pauseOverlays,
    });

    const [editRowsModel, setEditRowsModel] = React.useState({});
    const [rowSelectionModel, setRowSelectionModel] = React.useState([]);
    const deleteFiles = useDeleteFiles();

    const handleDeleteFiles = React.useCallback((fileIds) => {
        if (!fileIds || fileIds.length === 0) {
            return;
        }
        deleteFiles.mutate(fileIds, {
            onSuccess: () => {
                refreshCompanyFiles();
            },
        });
    }, [deleteFiles, refreshCompanyFiles]);

    /// for checking user data to allow user to download if policy is accepted or not
    const userdata = useSelector((state)=> state.UserReducer.user);
    const pdf = useSelector((state)=> state.FileReducer.PDFfile.file);
    const [pdfUrl, setPdfUrl] = React.useState(`${pdf}`);
    const [pdfStatus, setPdfStatus] = React.useState();

    function showPDF(cellValues) {
        handleOpen();
    };

    const [open, setOpen] = React.useState(false);
    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);
    function handleOpenBotmarkEstimate(x){
        setOpenBotmarkEstimate(true)
        setBotmarkEstimateObj(x);
        fetchValidationFile.mutate(x.id)
    };
    function handleCloseBotmarkEstimate(){
        setOpenBotmarkEstimate(false);
        setBotmarkEstimateObj({});
        dispatch({ type: "VALIDATION_RESET" });
        refreshCompanyFiles();
    };

    const handleValidationAccepted = React.useCallback((fileId) => {
        // Fresh accepts sort near the top; stay on page 0 so the refreshed row is findable.
        refreshCompanyFiles();
        setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
        setPendingBotmarkEstimateFileId(fileId);
    }, [refreshCompanyFiles]);

    React.useEffect(() => {
        if (!pendingBotmarkEstimateFileId) {
            return;
        }
        const row = files.find((file) => file.id === pendingBotmarkEstimateFileId);
        if (row?.isAccepted === 1 && row.botmarkIsInvoiceAccepted === 1 && row.botmarkInvoiceValues) {
            handleOpenBotmarkEstimate(row);
            setPendingBotmarkEstimateFileId(null);
        }
    }, [files, pendingBotmarkEstimateFileId]);

    function handleOpenValidationView (){
        setValidationView(true);
    };
    function handleCloseValidationView (){
        setValidationView(false);
        dispatch({ type: "VALIDATION_RESET" });
    };

    let userToUpdate = {
        Id: userdata.id,
        hasAcceptedUploadPolicy: userdata.hasAcceptedUploadPolicy,
        hasAcceptedDownloadPolicy: userdata.hasAcceptedDownloadPolicy,
        hasConfirmedEmail: userdata.hasConfirmedEmail,
        hasReadPrivacyPolicy: userdata.hasReadPrivacyPolicy,
    };

    function changePDFStatus(event){
        setPdfStatus(!pdfStatus);
        if (event === true) {
            userToUpdate = {
                Id: userdata.id,
                hasAcceptedUploadPolicy: userdata.hasAcceptedUploadPolicy,
                hasAcceptedDownloadPolicy: 1,
                hasConfirmedEmail: userdata.hasConfirmedEmail,
                hasReadPrivacyPolicy: userdata.hasReadPrivacyPolicy,
            }
            updateAgreement.mutate(userToUpdate);
            handleClose();
        }
        else if (event === false) {
            userToUpdate = {
                Id: userdata.id,
                hasAcceptedUploadPolicy: userdata.hasAcceptedUploadPolicy,
                hasAcceptedDownloadPolicy: 0,
                hasConfirmedEmail: userdata.hasConfirmedEmail,
                hasReadPrivacyPolicy: userdata.hasReadPrivacyPolicy,
            }
            updateAgreement.mutate(userToUpdate);
        }
    };

    const startDateInitialState = dayjs().subtract(1, "years").startOf('day');
    const endDateInitialState = dayjs().endOf('day');
    const [exportStartDate, setExportStartDate] = React.useState(dayjs(startDateInitialState).format());
    const [exportEndDate, setExportEndDate] = React.useState(dayjs(endDateInitialState).format());

    const setPdfUrlIfMounted = React.useCallback((url) => {
        if (isMountedRef.current) {
            setPdfUrl(url);
        }
    }, []);

    React.useEffect(() => {
        if (!companyId) {
            return;
        }

        if (userdata.hasAcceptedDownloadPolicy === 0) {
            if (isMountedRef.current) setPdfStatus(false);
        } else if (userdata.hasAcceptedDownloadPolicy === 1) {
            if (isMountedRef.current) setPdfStatus(true);
        }

        // Skip starting a new download while overlays are open — do not tie this to
        // deps that restart the fetch on every overlay close (races nav tab unmount).
        if (pauseOverlays) {
            return;
        }

        const controller = new AbortController();
        fetchPolicyPdf.mutate({
            pdfType: 2,
            setPdfUrl: setPdfUrlIfMounted,
            signal: controller.signal,
        });

        return () => {
            controller.abort();
        };
    }, [companyId, customerId, exportEndDate, exportStartDate, pauseOverlays, setPdfUrlIfMounted, userdata.hasAcceptedDownloadPolicy]);

    const handleDownload = (file) => {
        downloadPaid.mutate(file);
    };
    const handleEditRowsModelChange = React.useCallback((model) => {
        setEditRowsModel(model);
    }, []);

    const handleSave = () => {
        const objId = Object.keys(editRowsModel)[0];
        const obj = Object.values(editRowsModel)[0];
        const item = {
            id: objId,
            Name: obj.name.value
        };
        updateFileName.mutate(item, {
            onSuccess: (id) => {
                fetchValidationFile.mutate(id);
                refreshCompanyFiles();
            },
        });
    };

    function handleAcceptAndDownload(item){
        let acceptInvoice = {
            id: item.botmarkEstimateId,
            isInvoiceAccepted: 5,
            OpenInvoice: true,
        };
        updateInvoiceStatus.mutate({
            invoice: acceptInvoice,
            currentStatus: item.botmarkIsInvoiceAccepted,
            item,
            onPaidDownload: (paidItem) => downloadPaid.mutate(paidItem),
        });
    };


    function multiDelete(items){
        const fileIds = items;
        swal({
            title: "DELETE FILE(S)",
            text: `Delete ${fileIds.length} file(s) forever? This cannot be undone.`,
            icon: "warning",
            buttons: true,
            dangerMode: false,
        })
        .then((willUpdate) => {
            if (willUpdate) {
                handleDeleteFiles(fileIds);
            } else {
                swal({
                    title: `File(s) not Deleted.`
                });
        }})
    };

    const planJobStatusContext = React.useMemo(() => ({
        canManagePlans,
        company,
        companyData,
        hasAcceptedDownloadPolicy: userdata.hasAcceptedDownloadPolicy === 1,
        fileStorageCount,
    }), [canManagePlans, company, companyData, userdata.hasAcceptedDownloadPolicy, fileStorageCount]);

    const columns = [
        { field: 'name', headerName: 'Project Name', width: 250, headerClassName: 'header', editable: false},
        { field: 'jobStatus', headerName: 'Status', width: 225, headerClassName: 'header',
            valueGetter: (_value, row) => getPlanTableJobStatusSortLabel(row, planJobStatusContext),
            renderCell: (params) => (
                <PlanTableJobStatusCell
                    row={params.row}
                    status={resolvePlanTableJobStatus(params.row, planJobStatusContext)}
                    handlers={{
                        onOpenValidation: (row) => {
                            fetchValidationFile.mutate(row.id);
                            setReview(true);
                        },
                        onOpenBotmarkEstimate: (row) => {
                            fetchValidationFile.mutate(row.id);
                            handleOpenBotmarkEstimate(row);
                        },
                        onShowPdf: showPDF,
                        onDownload: handleDownload,
                        onAcceptAndDownload: handleAcceptAndDownload,
                    }}
                />
            )},
        { field: 'updated', headerName: 'Updated', width: 175, headerClassName: 'header', renderCell: (params) => {
            let date = new Date(params.row.updated);
            return date.toLocaleString()
        }},
        { field: 'customerName', headerName: 'Customer Name', width: 225, headerClassName: 'header', editable: false},
        { field: 'validationStatus', headerName: 'Validation & Summary', width: 240, headerClassName: 'header', sortable: false,
            renderCell: (cellValues) => {
                if(cellValues.row.isAccepted === 1 &&
                    (cellValues.row.isProcessed === 1 ||
                    cellValues.row.isProcessed === 3 ||
                    cellValues.row.isProcessed === 4) && cellValues.row.sequencingErrorCode <= 1){
                    cellValues.row.validationStatus = "Validation";
                    return(
                        <Button variant="contained" sx={{ bgcolor: `info.light`}}
                            onClick={() => {
                                fetchValidationFile.mutate(cellValues.row.id)
                                handleOpenValidationView();
                        }}>
                            View V & S
                        </Button>
                    )
                } else if (cellValues.row.isProcessed == 2){
                    cellValues.row.validationStatus = "Skipped Processing"
                    return(
                        <Chip icon={<AlertCircle/>} variant="outlined" color="warning" label="SKIPPED PROCESSING"/>
                        )
                } else if (cellValues.row.sequencingErrorCode == 88){
                    cellValues.row.validationStatus = "Processing Failed"
                    return(
                        <Button disabled variant="contained" color="error">
                            Processing Failed
                        </Button>
                    )
                } else if (cellValues.row.sequencingErrorCode > 1 && cellValues.row.sequencingErrorCode != 20){
                    cellValues.row.validationStatus = "Critical Error"
                    return(
                        <Button variant="contained" color="error"
                            onClick={() => {
                                fetchValidationFile.mutate(cellValues.row.id)
                                handleOpenValidationView();
                        }}>
                            View V & S (Critical)
                        </Button>
                    )
                } else if (cellValues.row.sequencingErrorCode == 20){
                    cellValues.row.validationStatus = "Non Critical Error";
                    return(
                        <Button variant="contained" color="warning"
                            onClick={() => {
                                fetchValidationFile.mutate(cellValues.row.id)
                                handleOpenValidationView();
                        }}>
                            View V & S (Non Critical)
                        </Button>
                    )
                }
            }
        },
        { field: 'Delete', headerName: 'Delete', width: 125, headerClassName: 'header', sortable: false,
            renderCell: (cellValues) => {
                if (canManagePlans){
                    if (rowSelectionModel.length == 0){
                        if(cellValues.row.isProcessed === 0){
                            return(<></>)
                        }
                        else if(
                            cellValues.row.botmarkIsInvoiceAccepted === null||
                            cellValues.row.botmarkIsInvoiceAccepted === 0||
                            cellValues.row.botmarkIsInvoiceAccepted === 1||
                            cellValues.row.botmarkIsInvoiceAccepted === 2||
                            cellValues.row.botmarkIsInvoiceAccepted === 3||
                            cellValues.row.botmarkIsInvoiceAccepted === 4 && cellValues.row.isInvoiceAccepted != 5
                        ){
                            return(
                                <Button className="delBtn" variant="contained" sx={{ bgcolor: `error.light`}}
                                    onClick={() => {
                                        swal({
                                            title: "DELETE FILE",
                                            text: `Delete this file forever? This cannot be undone.`,
                                            icon: "warning",
                                            buttons: true,
                                            dangerMode: false,
                                        })
                                        .then((willUpdate) => {
                                            if (willUpdate) {
                                                const fileId = cellValues.row.id;
                                                handleDeleteFiles([fileId]);
                                            } else {
                                                swal({
                                                    title: `File not Deleted.`
                                                });
                                        }})
                                }}>
                                    Delete
                                </Button>
                            )
                        } else if(cellValues.row.botmarkIsInvoiceAccepted === 5){
                            return(
                                <Button className="delBtn" variant="contained" sx={{ bgcolor: `error.light`}}
                                    onClick={() => {
                                        swal({
                                            title: "DELETE FILE",
                                            text: `Delete this file forever? This cannot be undone.`,
                                            icon: "warning",
                                            buttons: true,
                                            dangerMode: false,
                                        })
                                        .then((willUpdate) => {
                                            if (willUpdate) {
                                                const fileId = cellValues.row.id;
                                                handleDeleteFiles([fileId]);
                                            } else {
                                                swal({
                                                    title: `File not Deleted.`
                                                });
                                        }})
                                }}>
                                    Delete
                                </Button>
                            )
                        }
                    } else {
                        return(
                            <Button disabled className="delBtn" variant="contained" sx={{ bgcolor: `error.light`}}>Delete</Button>
                        )
                    }
                }
            }
        },
    ];
    const rows = files;

    let dispatchSettings = {
        companyId: company?.id,
        page: paginationModel.page,
        pageSize: paginationModel.pageSize,
        customerId: customerSelect.userCompanyId,
        startDate: exportStartDate,
        endDate: exportEndDate,
    }

    return (
        <Box sx={{ '& .header': { backgroundColor: '#e2e2e2' }, height: '60vh' }}>
            {managePlans != true &&
                <p style={{textAlign:'left', backgroundColor:'lightblue', padding:5}}><b>Announcements:</b> {internalAnn}</p>
            }
            {managePlans == true &&
                <form style={{display:'flex', flexDirection:'column', alignItems: 'start' }}>
                    {/* <h4>Customer Select</h4> */}
                    <p><b>Select customer to further filter plans list.</b></p>
                    <FormControl fullWidth style={{width:'33%'}}>
                        <Autocomplete
                            disablePortal
                            value={selectedCustomer}
                            {...defaultProps}
                            renderInput={(params) =>
                                <TextField
                                    {...params}
                                    label="Companies"
                                />}
                            onChange={(event,newValue) => {
                                if(newValue == null){
                                    setSelectedCustomer(NA_CUSTOMER_OPTION)
                                }else{
                                    setSelectedCustomer(newValue)
                                }
                            }}
                        />
                    </FormControl>
                </form>
            }
            <div style={{margin:'5px', display:'flex', justifyContent:'space-between'}}>
                <p style={{textAlign:'left'}}><b>Current File Count: </b><StorageLimitIndicator count={fileStorageCount} /></p>
                <p style={{textAlign:'left'}}>
                    Select the plans you want to delete.
                    {rowSelectionModel.length > 0 ?
                        <Button
                            className="delBtn" variant="contained" sx={{ bgcolor: `error.light`}}
                            onClick={() => multiDelete(rowSelectionModel)}
                        >
                            Delete
                        </Button>
                        :
                        <Button
                            disabled
                            className="delBtn" variant="contained" sx={{ bgcolor: `error.light`}}
                        >
                            Delete
                        </Button>
                    }
                </p>
            </div>

            <ReviewPage status={review} change={setReview} onAccepted={handleValidationAccepted}/>
            <DataGrid
                style={{textAlign:'left'}}
                checkboxSelection
                onRowSelectionModelChange={(newRowSelectionModel) => {
                    setRowSelectionModel(newRowSelectionModel);
                }}
                rowSelectionModel={rowSelectionModel}
                isRowSelectable={(params) =>
                    (params.row.botmarkIsInvoiceAccepted === null ||
                    params.row.botmarkIsInvoiceAccepted === 1 ||
                    params.row.botmarkIsInvoiceAccepted === 2 ||
                    params.row.botmarkIsInvoiceAccepted === 3 ||
                    params.row.botmarkIsInvoiceAccepted === 5 ||
                    params.row.botmarkIsInvoiceAccepted === 4 && params.row.isInvoiceAccepted != 5) && params.row.isProcessed != 0
                }
                disableRowSelectionOnClick
                editMode="row"
                onRowEditCommit={handleSave}
                editRowsModel={editRowsModel}
                onEditRowsModelChange={handleEditRowsModelChange}
                columns={columns}
                rows={rows}
                loading={deleteFiles.isLoading}
                pageSizeOptions={[10, 25, 50, 100]}
                rowCount={rowCount}
                paginationMode="server"
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
            />
            <AgreementModal
                open={open}
                onClose={handleClose}
                title="Downloader Agreement"
                description="Read and accept the below User Agreement to download plans."
                pdfUrl={pdfUrl}
                checked={pdfStatus}
                onToggle={changePDFStatus}
                acceptLabel="Accept Download Policy"
            />
            <Modal
                open={openBotmarkEstimate}
                onClose={handleCloseBotmarkEstimate}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
                className="estimateModal"
                style={{height:'100%', width: '100%'}}
                hideBackdrop={true}
            >
                <div className="estimateDiv" style={{height:'100%', width: '100%', }}>
                    <BotmarkEstimateView
                        className="estimateDiv"
                        managePlans={managePlans}
                        handleCloseBotmarkEstimate={handleCloseBotmarkEstimate}
                        botmarkEstimateObj={botmarkEstimateObj}
                        dispatchSettings={dispatchSettings}
                    />
                </div>
            </Modal>
            <Modal
                open={validationView}
                onClose={handleCloseValidationView}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
                className="estimateModal"
                style={{height:'100%', width: '100%'}}
                hideBackdrop={true}
            >
                <div className="estimateDiv" style={{height:'100%', width: '100%', }}>
                    <StaticValidationView className="estimateDiv" handleCloseValidationView={handleCloseValidationView} />
                </div>
            </Modal>
        </Box>
    )
};

export default PlanTable;